import { useState } from "react";
import { Clock, RefreshCw, Star, Crown, ArrowLeft, LogIn, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { loadConnection } from "../lib/transport";
import { useAuth } from "../store/AuthContext";
import { PlanChooser } from "../components/onboarding/PlanChooser";
import { RegisterForm } from "../components/onboarding/RegisterForm";
import { CodeStep } from "../components/onboarding/CodeStep";
import { SignInPanel } from "../components/onboarding/SignInPanel";
import { markWelcome } from "../components/onboarding/WelcomeDialog";

const deviceId = () => { let d = localStorage.getItem("xpattern.device"); if (!d) { d = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, ""); localStorage.setItem("xpattern.device", d); } return d; };

const PLAN_META = {
  trial: { title: "7-Day Free Trial", icon: Clock, color: "text-[#f5c542]", blurb: "Full access to every feature for 7 days. No card needed." },
  monthly: { title: "Monthly · $79.99 / month", icon: RefreshCw, color: "text-[#4c9ffe]", blurb: "Unlimited cuts, full vehicle library, cancel anytime." },
  yearly: { title: "Yearly · $899.99 / year", icon: Star, color: "text-[#f0c945]", blurb: "Everything in Monthly, one payment for the whole year." },
};

// Shown over the disabled workspace until the customer picks a plan and verifies their email.
export const OnboardingShell = ({ title, icon: I, iconClass, subtitle, onBack, onClose, children, wide, noLogo, testId = "onboarding-dialog", action }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0c1017]/70 p-4 pt-12 backdrop-blur-[3px]" data-testid="onboarding-overlay" onMouseDown={(e) => { if (onClose && e.target === e.currentTarget) onClose(); }}>
    <div className={`glass-panel relative max-h-[92vh] w-full overflow-y-auto p-7 text-[#e8e9ec] ${wide ? "max-w-5xl" : "max-w-lg"}`} data-testid={testId}>
      {onClose && <button data-testid={`${testId}-close`} onClick={onClose} title="Close" className="absolute right-4 top-4 rounded-md p-1 text-[#9aa0a8] hover:bg-white/10 hover:text-[#e8e9ec]"><X className="h-4 w-4" /></button>}
      <div className="mb-4 flex items-start gap-3">
        {onBack && <button data-testid="onboarding-back" onClick={onBack} className="mt-0.5 text-[#9aa0a8] hover:text-[#e8e9ec]" title="Back"><ArrowLeft className="h-4 w-4" /></button>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[17px] font-semibold" data-testid="onboarding-title">{I && <I className={`h-4 w-4 ${iconClass || ""}`} />}{title}</div>
          {subtitle && <div className="mt-0.5 text-[12px] text-[#9aa0a8]">{subtitle}</div>}
        </div>
        {action}
        {!noLogo && <img src="/xpattern-x.png" alt="" className={`h-8 w-auto opacity-80 ${onClose ? "mr-8" : ""}`} />}
      </div>
      {children}
    </div>
  </div>
);

export default function AuthPage({ demo = false }) {
  const { signIn, setPreviewAs } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const connectCode = params.get("connect");
  const subscribeIntent = params.get("subscribe"); // from the demo-ended / reminder emails: sign in, then the plan + card opens
  const [step, setStep] = useState(subscribeIntent ? "signin" : "choose"); // choose | register | code | signin — always open on the plan chooser, even right after install
  const [plan, setPlan] = useState("trial");
  const [form, setForm] = useState(null);
  const [devCode, setDevCode] = useState("");
  const meta = PLAN_META[plan];

  const register = async (code, f = form) => {
    if (demo) { toast.info(`Preview only — no account was created. Showing the next screen a ${plan === "trial" ? "trial" : "paying"} customer sees.`); if (plan === "trial") markWelcome("trial"); setPreviewAs(plan === "trial" ? "trial" : "pending"); return; }
    // installed the desktop app first? its pairing code is in the URL (/start?connect=CODE) — sign up straight onto that connector
    const d = await api.register({ ...f, email_code: code || "", plan, shop_code: connectCode || loadConnection().shopCode, device_id: deviceId(), ref: localStorage.getItem("xp.ref") || "" });
    localStorage.removeItem("xp.ref");
    localStorage.setItem("xpattern.loginMode", "password");  // the account has a password from day one — sign-in opens on it here
    if (plan === "trial") markWelcome("trial");
    signIn(d);
    if (plan === "trial") toast.success("Welcome to Xpattern! Your 7-day free trial has started", { duration: 8000 });
  };

  if (step === "choose") return (
    <OnboardingShell wide title="Welcome to Xpattern — choose your plan" icon={Crown} iconClass="text-[#f5a524]" subtitle="Pick how you'd like to start. You can upgrade from the trial at any time."
      action={<button data-testid="onboarding-signin-btn" onClick={() => setStep("signin")} className="glass-cta mr-3 flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold" style={{ "--glass-accent": "#2f80ff" }}><LogIn className="h-3.5 w-3.5" /> Already a member? Sign in</button>}>
      <PlanChooser onPick={(p) => { setPlan(p); setStep("register"); }} onSignIn={() => setStep("signin")} />
    </OnboardingShell>
  );
  if (step === "signin") return (
    <OnboardingShell title={subscribeIntent ? "Sign in to subscribe" : "Sign in"} icon={LogIn} iconClass="text-[#4c9ffe]" subtitle={subscribeIntent ? `Sign in with the one-time code we email you — the ${subscribeIntent === "yearly" ? "Yearly" : "Monthly"} plan and secure payment open right after.` : connectCode ? "Xpattern is installed on this computer. Sign in and your plan activates automatically." : "Welcome back."} onBack={() => setStep("choose")}>
      <SignInPanel demo={demo} initialEmail={params.get("email") || ""} />
    </OnboardingShell>
  );
  if (step === "register") return (
    <OnboardingShell title={meta.title} icon={meta.icon} iconClass={meta.color} subtitle={`${meta.blurb} Tell us about your shop:`} onBack={() => setStep("choose")}>
      <RegisterForm plan={plan} onCodeSent={(f, dc) => { setForm(f); setDevCode(dc || ""); setStep("code"); }} onPaidSubmit={(f) => { setForm(f); return register("", f); }} />
    </OnboardingShell>
  );
  return (
    <OnboardingShell title="Check your email" icon={meta.icon} iconClass={meta.color} subtitle={meta.title} onBack={() => setStep("register")}>
      <CodeStep email={form.email} devCode={devCode} onVerify={register} onResend={() => api.otpRequest(form.email, "signup", form.name)} />
    </OnboardingShell>
  );
}
